/**
 * The app's ONLY value import of Mixpanel. Import `mixpanel` from here, never
 * from `mixpanel-browser` directly.
 *
 * Why this file exists: the package's default entry (`mixpanel-browser` →
 * `dist/mixpanel.module.js`) bundles the rrweb session recorder (~65-80 KB
 * gzipped) into whatever chunk imports it — and `instrumentation-client.ts`
 * runs before hydration, so that weight sat on the boot path of every page.
 * The async-recorder build is the same SDK with `loadAsync`: the recorder is
 * a separate script, fetched from `recorder_src` only when a session
 * recording starts — which `startSessionRecordingAfterLoad` defers until the
 * page has settled.
 *
 * Import the prebuilt `dist/…cjs.js`, NOT `src/loaders/loader-module-with-
 * async-recorder`: the src tree is extensionless ESM that Node cannot load,
 * and Playwright's runner reaches this file through `@generated/types` → the
 * RTK api → analytics, so the src path made every E2E spec fail to load.
 *
 * Both `instrumentation-client.ts` (which calls `mixpanel.init`) and
 * `lib/analytics.ts` (identify/track/reset/recording pauses) MUST import this
 * same module. Two different Mixpanel entry points would mean two instances,
 * one never initialised, and every event sent through it silently dropped.
 *
 * `recorder_src` points at our own copy of the recorder (see
 * MIXPANEL_RECORDER_SRC), never the default `cdn.mxpnl.com` URL: that one is
 * unpinned (it can change under a deployed build) and runs third-party code
 * on a payments origin.
 */
import type { OverridedMixpanel } from "mixpanel-browser";
import asyncRecorderMixpanel from "mixpanel-browser/dist/mixpanel-with-async-recorder.cjs.js";

import { type AfterLoadIdleWindow, runAfterLoadIdle } from "./afterLoadIdle";

// The dist build ships its own copy of the package's .d.ts, so its default
// export is a structurally identical but separate `OverridedMixpanel` that
// the augmentation in `types/mixpanel.d.ts` (declared on "mixpanel-browser")
// does not reach. Re-type it as the package's own interface so
// pause/resume_session_recording stay typed.
const mixpanel = asyncRecorderMixpanel as OverridedMixpanel;

/**
 * Self-hosted, same-origin, version-pinned copy of `mixpanel-browser/dist/
 * mixpanel-recorder.min.js` — never the unversioned `cdn.mxpnl.com` default.
 * The filename carries the library version, so a Mixpanel bump fails
 * `mixpanelClient.test.ts` until it is re-vendored:
 *   bun run --cwd apps/web vendor:mixpanel-recorder
 * which copies the installed recorder, removes the old version and rewrites
 * this constant. Commit `public/vendor/` together with this file.
 */
export const MIXPANEL_RECORDER_SRC = "/vendor/mixpanel-recorder-2.73.0.min.js";

/**
 * The session-recording rate: every session (the 100% sampling invariant,
 * `.claude/rules/web/analytics.md`). Applied by
 * `startSessionRecordingAfterLoad`, not at `mixpanel.init` — see there.
 */
export const MIXPANEL_RECORD_SESSIONS_PERCENT = 100;

type RecordingControls = Pick<
  OverridedMixpanel,
  "set_config" | "start_session_recording"
>;

/**
 * Start Mixpanel session recording once the page has settled (after `load`
 * plus idle, 10 s ceiling — `lib/afterLoadIdle.ts`), so the recorder script
 * (rrweb, our self-hosted MIXPANEL_RECORDER_SRC) never competes with the
 * first load.
 * Event tracking (autocapture, pageviews, identify) runs from `mixpanel.init`
 * as before; only the recorder is deferred.
 *
 * `mixpanel.init` therefore runs with `record_sessions_percent: 0`. In
 * mixpanel-browser 2.73 (src/mixpanel-core.js) init ends in
 * `_check_and_start_session_recording()`, which with a non-zero percent
 * injects the recorder at once (L492-494); `start_session_recording()` calls
 * it with `force_start`, which loads and starts regardless of the percent
 * (L505-507). Setting the percent to 100 first keeps every later sampling
 * decision at 100%: `reset()` (our logout) stops the recording and re-samples
 * from the config (L1679-1688), so a 0 left in place would leave every
 * post-logout session unrecorded.
 *
 * Unchanged by deferral: the 5-minute idle stop/restart lives inside the
 * recorder (recorder/recorder.js `onIdleTimeout` → `resetRecording`), and
 * `pause/resume_session_recording` (lib/analytics.ts) no-op until the
 * recorder exists (L516-528). With percent 0, init still checks the SDK's
 * IndexedDB recording registry (`_should_load_recorder`, L442-466,
 * L496-501) and loads the recorder at once in two cases: a reload in a tab
 * whose recording is still live (it resumes that recording), and ANY load,
 * fresh tab included, while the registry holds an expired recording from
 * any tab (it loads to flush it). So a trace showing the recorder fetched
 * during boot is not by itself a regression. Calling start afterwards is
 * idempotent: `resumeRecording` finds the live recording and
 * `startRecording` skips ("Recording already in progress").
 *
 * Returns a disposer (tests); the caller keeps none, the schedule runs once.
 */
export function startSessionRecordingAfterLoad({
  instance = mixpanel,
  win = typeof window === "undefined" ? undefined : window,
}: {
  instance?: RecordingControls;
  win?: AfterLoadIdleWindow;
} = {}): () => void {
  if (!win) return () => {};
  return runAfterLoadIdle(
    () => {
      instance.set_config({
        record_sessions_percent: MIXPANEL_RECORD_SESSIONS_PERCENT,
      });
      instance.start_session_recording();
    },
    { win }
  );
}

export default mixpanel;
